import { ParentCompanyBadge } from "@/components/ParentCompanyBadge";
import { useLandingPageImages } from "@/hooks/useLandingPageImages";
import { CARE_NEEDS, FOR_WHO_OPTIONS } from "@/lib/careMatch";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type FormEvent } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Heart, Droplet, SquarePlus, Users, Home, Moon, BedDouble, CircleHelp, ChevronLeft, ChevronRight,
  ChevronDown, Plus, MapPin, MessageSquare, Smile, Wallet, Mail, UserRound, Coffee, Camera, Globe, Menu, X, Check,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { TRACKING_WINDOW_EVENTS } from "@/config/tracking";
import { SiteBrand } from "@/components/SiteBrand";
import { NavProfileAvatar } from "@/components/NavProfileAvatar";
import { useAuth } from "@/contexts/AuthContext";
import "@/styles/home-apple.css"; // scoped onder .ha


/* ─── Routes (alle bestaand) ─────────────────────────────────────────── */
export const R = {
  hulpvraag: "/plaats-advertentie",
  zoeken: "/zoek-zorgverlener",
  hoe: "/hoe-werkt-het",
  voorZv: "/voor-zorgverleners",
  verhalen: "/nieuws",
  login: "/login",
  aanmelden: "/login?tab=register&type=caregiver",
  vacatures: "/vacatures",
  abonnementen: "/abonnementen",
  zorgsoorten: "/zorgsoorten",
  pgb: "/pgb-zorgverleners-vinden",
  pgbAanvragen: "/pgb-aanvragen",
  contact: "/contact",
  voorwaarden: "/voorwaarden",
  partners: "/gemeente-samenwerking",
};
const CONTACT_EMAIL = "perfectezorgmatch@deerpeter.nl";
const CONTACT_HREF = `mailto:${CONTACT_EMAIL}`;
const IC = { strokeWidth: 1.4 } as const;

/* ─── Carrousel ─────────────────────────────────────────────────────── */
function Carousel({ children, resetKey, label }: { children: ReactNode; resetKey?: string; label: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 4);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.scrollTo({ left: 0, behavior: "auto" });
    update();
  }, [resetKey, update]);

  useEffect(() => {
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [update]);

  const step = (dir: 1 | -1) => {
    const el = ref.current;
    const first = el?.firstElementChild as HTMLElement | null;
    if (!el || !first) return;
    el.scrollBy({ left: dir * (first.offsetWidth + 20), behavior: "smooth" });
  };

  return (
    <>
      <div ref={ref} className="ha-track" onScroll={update} role="region" aria-label={label} tabIndex={0}>
        {children}
      </div>
      <div className="ha-wrap">
        <div className="ha-arrows">
          <button type="button" className="ha-arrow" onClick={() => step(-1)} disabled={atStart} aria-label="Vorige">
            <ChevronLeft size={20} />
          </button>
          <button type="button" className="ha-arrow" onClick={() => step(1)} disabled={atEnd} aria-label="Volgende">
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
    </>
  );
}

/* ─── 1. Nav ─────────────────────────────────────────────────────────── */
export function HomeNav() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [pricesOpen, setPricesOpen] = useState(false);
  const links: [string, string][] = [
    ["Zorgverleners", R.zoeken],
    ["Hulpvragen", R.vacatures],
    ["Hoe het werkt", R.hoe],
    ["Verhalen", R.verhalen],
    ...(!user ? [["Inloggen", R.login] as [string, string]] : []),
  ];
  const renderLink = ([label, to]: [string, string]) =>
    to.startsWith("#") ? (
      <a key={label} href={to} onClick={() => setOpen(false)}>{label}</a>
    ) : (
      <Link key={label} to={to} onClick={() => setOpen(false)}>{label}</Link>
    );
  return (
    <>
      <nav className="ha-nav" aria-label="Hoofdmenu">
        <div className="ha-wrap ha-nav-inner">
          <Link to="/" className="ha-logo" aria-label="Perfecte Zorgmatch, home">
            <SiteBrand />
          </Link>
          <div className="ha-nav-links">
            {links.slice(0, 2).map(renderLink)}
            <div className="ha-nav-group">
              <Link to={R.voorZv}>Voor zorgverleners</Link>
              <button type="button" className="ha-nav-subtoggle" aria-label="Submenu voor zorgverleners" aria-expanded={pricesOpen} onClick={() => setPricesOpen((value) => !value)}><ChevronDown size={14} aria-hidden="true" /></button>
              <div className={`ha-nav-submenu ${pricesOpen ? "is-open" : ""}`}>
                <Link to={R.abonnementen} onClick={() => setPricesOpen(false)}>Prijzen</Link>
              </div>
            </div>
            {links.slice(2).map(renderLink)}
          </div>
          <div className="ha-nav-right">
            {user && (
              <Link to="/mijn-account" aria-label="Mijn account" className="inline-flex shrink-0 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <NavProfileAvatar />
              </Link>
            )}
            <button
              type="button"
              className="ha-burger"
              aria-label={open ? "Menu sluiten" : "Menu openen"}
              aria-expanded={open}
              onClick={() => setOpen((o) => !o)}
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
            <Link to={R.hulpvraag} className="ha-btn ha-btn-sm">Plaats hulpvraag</Link>
          </div>
        </div>
      </nav>
      <div className={`ha-mobile-menu ${open ? "is-open" : ""}`}>
        {open && <>
          {links.slice(0, 2).map(renderLink)}
          <Link to={R.voorZv} onClick={() => setOpen(false)}>Voor zorgverleners</Link>
          <Link to={R.abonnementen} onClick={() => setOpen(false)} className="ha-mobile-subitem">Prijzen</Link>
          {links.slice(2).map(renderLink)}
        </>}
      </div>
    </>
  );
}

/* ─── 3. Hero ────────────────────────────────────────────────────────── */
function Hero() {
  const { getImage, loading } = useLandingPageImages("homepage");
  const adminHero = getImage("hero");
  const customUrl = !loading && adminHero?.image_url ? adminHero.image_url : null;
  return (
    <section className="ha-hero">
      <div className="ha-wrap ha-hero-inner">
        <h1>PGB-zorg vinden die bij je past</h1>
        <p className="ha-hero-subtitle">De Perfecte Zorgmatch verbindt liefdevolle zorgverleners met cliënten.</p>
        <div className="ha-hero-actions">
          <Link to={R.hulpvraag} className="ha-btn ha-hero-button">Plaats een hulpvraag</Link>
          <Link to={R.aanmelden} className="ha-hero-signup">Meld je aan als zorgverlener ›</Link>
        </div>
        {customUrl && (
          <img
            className="ha-hero-photo"
            src={customUrl}
            sizes="(max-width: 767px) calc(100vw - 32px), (max-width: 1240px) calc(100vw - 40px), 1200px"
            width="1200"
            height="600"
            fetchPriority="high"
            alt={adminHero?.alt_text || "Zorgverlener en cliënt samen thuis – PGB zorg via De Perfecte Zorgmatch"}
          />
        )}
        <a className="ha-hero-all" href="#zorgsoorten">Bekijk alle zorgsoorten ›</a>
      </div>
    </section>
  );
}

/* ─── 4. Zorgsoorten ─────────────────────────────────────────────────── */
type Tab = "Alle zorg" | "Thuis" | "Begeleiding" | "Dag en nacht";
const CARE: { title: string; slug: string; tab: Tab; text: string; tags: string[]; bg: string; icon: ReactNode; isNew?: boolean }[] = [
  { title: "Persoonlijke verzorging", slug: "persoonlijke-verzorging", tab: "Thuis", text: "Hulp bij wassen en aankleden. Zodat je de dag goed begint.", tags: ["Wassen", "Aankleden", "Medicatie"], bg: "#ebe5da", icon: <Droplet size={56} {...IC} /> },
  { title: "Verpleging aan huis", slug: "verpleegkundige", tab: "Thuis", text: "De zorg die je nodig hebt. In je eigen vertrouwde omgeving.", tags: ["Wondzorg", "Injecties", "Na opname"], bg: "#dfe8e3", icon: <SquarePlus size={56} {...IC} /> },
  { title: "Begeleiding", slug: "begeleiding", tab: "Begeleiding", text: "Structuur, rust en iemand die met je meedenkt.", tags: ["Dagritme", "Afspraken", "Sociaal"], bg: "#e5e0ea", icon: <Users size={56} {...IC} /> },
  { title: "Dementiezorg", slug: "dagbesteding", tab: "Begeleiding", text: "Geduld, herkenning en elke keer een vertrouwd gezicht.", tags: ["Toezicht", "Activiteiten", "Rust"], bg: "#ede3dc", icon: <Heart size={56} {...IC} /> },
  { title: "Inwonende zorg", slug: "thuiszorg", tab: "Dag en nacht", text: "Iemand die met je meewoont. Dag en nacht dichtbij.", tags: ["24 uur", "Samenwonen", "Nacht"], bg: "#dde4ea", icon: <Moon size={56} {...IC} />, isNew: true },
  { title: "Huishoudelijke hulp", slug: "huishoudelijk", tab: "Thuis", text: "Een schoon, opgeruimd huis. Zonder dat jij je druk maakt.", tags: ["Schoonmaak", "Was", "Boodschappen"], bg: "#e8e6da", icon: <Home size={56} {...IC} /> },
  { title: "Respijtzorg", slug: "thuiszorg", tab: "Dag en nacht", text: "Even ademhalen als mantelzorger. Wij zorgen dat de zorg doorgaat.", tags: ["Logeren", "Weekend", "Vakantie"], bg: "#e3e8dc", icon: <BedDouble size={56} {...IC} /> },
];

function CareTypes() {
  const [tab, setTab] = useState<Tab>("Alle zorg");
  const cards = CARE.filter((c) => tab === "Alle zorg" || c.tab === tab);
  return (
    <section id="zorgsoorten" className="ha-sec ha-soft" style={{ scrollMarginTop: 64 }}>
      <div className="ha-wrap ha-sec-head">
        <div className="ha-care-heading"><h2 className="ha-h2">Ontdek alle zorgsoorten.</h2><Link to={R.zorgsoorten} className="ha-link-more">Bekijk alle zorgsoorten ›</Link></div>
        <div className="ha-seg" role="group" aria-label="Filter zorgsoorten">
          {(["Alle zorg", "Thuis", "Begeleiding", "Dag en nacht"] as Tab[]).map((t) => (
            <button key={t} type="button" aria-pressed={tab === t} onClick={() => setTab(t)}>{t}</button>
          ))}
        </div>
      </div>
      <Carousel resetKey={tab} label="Zorgsoorten">
        {cards.map((c) => (
          <article key={c.title} className="ha-care">
            <div className="ha-care-img" style={{ background: c.bg }} aria-hidden="true">{c.icon}</div>
            <div className="ha-tags">{c.tags.map((t) => <span key={t}>{t}</span>)}</div>
            <h3>
              {c.title}
              {c.isNew && <span className="ha-new" style={{ display: "block", fontSize: 13, marginTop: 4 }}>Nieuw</span>}
            </h3>
            <p>{c.text}</p>
            <div className="ha-care-foot">
              <Link to={R.zoeken} className="ha-btn ha-btn-md">Vind zorgverlener</Link>
              <Link to={`/zorgsoorten/${c.slug}`} aria-label={`Meer info over ${c.title}`}>Meer info ›</Link>
            </div>
          </article>
        ))}
      </Carousel>
    </section>
  );
}

/* ─── 5. Waarom ──────────────────────────────────────────────────────── */
type Why = { icon: ReactNode; label: string; h: ReactNode; p: string; to: string };
const em = (s: string) => <em>{s}</em>;
const WHY_CLIENT: Why[] = [
  { icon: <MapPin size={30} {...IC} />, label: "In jouw regio", h: <>Zorgverleners {em("bij jou in de buurt")}.</>, p: "Vul je postcode in en zie wie er in jouw wijk zorg geeft.", to: R.zoeken },
  { icon: <MessageSquare size={30} {...IC} />, label: "Direct contact", h: <>Geen tussenpersoon. {em("Rechtstreeks")} met je zorgverlener.</>, p: "Stel je vragen, maak kennis en spreek samen af wat bij je past.", to: R.hoe },
  { icon: <Smile size={30} {...IC} />, label: "Echte profielen", h: <>Weet wie er binnenkomt. {em("Echte gezichten")}.</>, p: "Ieder profiel met foto, ervaring en werkgebied. Zo kies je met vertrouwen.", to: R.zoeken },
  { icon: <CircleHelp size={30} {...IC} />, label: "Hulpvraag", h: <>Eén hulpvraag. {em("Zorgverleners komen naar jou")}.</>, p: "Vertel wat je nodig hebt. Wie past, reageert.", to: R.hulpvraag },
  { icon: <Wallet size={30} {...IC} />, label: "PGB", h: <>Zorg met je PGB, {em("zelf geregeld")}.</>, p: "Jij kiest wie je zorg geeft. Zonder wachtlijst van een bureau.", to: R.pgb },
  { icon: <Mail size={30} {...IC} />, label: "Persoonlijk", h: <>Vragen? {em("Mail ons gerust")}.</>, p: `We denken met je mee via ${CONTACT_EMAIL}.`, to: CONTACT_HREF },
];
const WHY_CARE: Why[] = [
  { icon: <MapPin size={30} {...IC} />, label: "Jouw regio", h: <>Cliënten zoeken {em("in jouw regio")}.</>, p: "Ook in jouw regio? Ja. Hulpvragen uit heel Nederland, gefilterd op jouw werkgebied.", to: "/vacatures" },
  { icon: <MessageSquare size={30} {...IC} />, label: "Reageren", h: <>Reageer {em("onbeperkt")} op hulpvragen.</>, p: "Met een abonnement reageer je op elke hulpvraag die bij je past.", to: R.abonnementen },
  { icon: <Globe size={30} {...IC} />, label: "Jouw profiel", h: <>Je profiel is {em("je eigen website")}.</>, p: "Gevonden worden zonder zelf een website te bouwen of te onderhouden.", to: R.voorZv },
  { icon: <UserRound size={30} {...IC} />, label: "Direct", h: <>Geen bemiddeling. {em("Directe PGB-cliënten")}.</>, p: "Jij en je cliënt maken samen afspraken. Zonder bureau ertussen.", to: R.voorZv },
  { icon: <Camera size={30} {...IC} />, label: "Zichtbaar", h: <>Jouw verhaal, {em("in beeld")}.</>, p: "Abonnees krijgen een fotoshoot en kort interview voor hun profiel en onze verhalen.", to: R.verhalen },
  { icon: <Coffee size={30} {...IC} />, label: "Persoonlijk", h: <>Kom langs {em("voor koffie")}.</>, p: "Ons kantoor staat in Zwolle. We maken graag kennis.", to: R.contact },
];

function PlusLink({ to, label }: { to: string; label: string }) {
  const inner = <Plus size={18} strokeWidth={2.2} aria-hidden="true" />;
  return to.startsWith("mailto:") ? (
    <a href={to} className="ha-plus" aria-label={label}>{inner}</a>
  ) : (
    <Link to={to} className="ha-plus" aria-label={label}>{inner}</Link>
  );
}

function WhyUs() {
  const [mode, setMode] = useState<"zoek" | "geef">("zoek");
  const cards = mode === "zoek" ? WHY_CLIENT : WHY_CARE;
  return (
    <section className="ha-sec" id="hoe">
      <div className="ha-wrap ha-sec-head">
        <h2 className="ha-h2">
          {mode === "zoek" ? <>Waarom zorg vinden via<br />Perfecte Zorgmatch.</> : <>Waarom zorgverleners kiezen<br />voor Perfecte Zorgmatch.</>}
        </h2>
        <div className="ha-seg ha-seg-light" role="group" aria-label="Kies je situatie">
          <button type="button" aria-pressed={mode === "zoek"} onClick={() => setMode("zoek")}>Ik zoek zorg</button>
          <button type="button" aria-pressed={mode === "geef"} onClick={() => setMode("geef")}>Ik geef zorg</button>
        </div>
      </div>
      <Carousel resetKey={mode} label="Waarom Perfecte Zorgmatch">
        {cards.map((c) => (
          <article key={c.label} className="ha-why">
            <span style={{ color: "var(--ha-accent)" }} aria-hidden="true">{c.icon}</span>
            <p className="ha-why-label">{c.label}</p>
            <h3>{c.h}</h3>
            <p>{c.p}</p>
            {c.label === "PGB" && <Link to={R.pgbAanvragen} className="underline underline-offset-4">PGB aanvragen: bekijk de stappen</Link>}
            <PlusLink to={c.to} label={`Meer over: ${c.label}`} />
          </article>
        ))}
      </Carousel>
    </section>
  );
}

/* ─── 6. Gezichten ───────────────────────────────────────────────────── */
type PublicProfile = {
  id: string; first_name: string; city: string | null; profile_photo_url: string | null;
  slug: string | null; quote: string | null; profile_text: string | null; tags: string[] | null; subscription_tier: string;
};
type Face = { key: string; role: string; quote: string; name: string; photo: string | null; to: string; bg: string };
const FACE_BG = ["#e8e0d5", "#dbe5de", "#e3dde5"];
const shortLine = (s: string | null | undefined) => {
  const clean = (s ?? "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  if (!clean) return "";
  const first = clean.split(/(?<=[.!?])\s/)[0];
  return first.length <= 60 ? first : `${first.slice(0, 57).replace(/\s+\S*$/, "")}…`;
};
const pretty = (s: string) => s.replace(/[-_]/g, " ").replace(/^\w/, (c) => c.toUpperCase());

function Faces() {
  const { data: profiles = [] } = useQuery({
    queryKey: ["homepage-public-caregivers", null],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("get-public-caregiver-profiles", {});
      if (error) throw error;
      return (data?.profiles ?? []) as PublicProfile[];
    },
    staleTime: 10 * 60 * 1000,
  });

  const faces = useMemo<Face[]>(() => {
    return profiles
      .slice(0, 25)
      .map((p, i) => ({
        key: p.id,
        role: [p.tags?.[0] ? pretty(p.tags[0]) : "Zorgverlener", p.city].filter(Boolean).join(" · "),
        quote: shortLine(p.quote || p.profile_text),
        name: p.first_name,
        photo: p.profile_photo_url,
        to: p.slug ? `/zorgverlener/${p.slug}` : R.zoeken,
        bg: FACE_BG[i % 3],
      }));
  }, [profiles]);

  return (
    <section className="ha-sec ha-soft" id="zorgverleners">
      <div className="ha-wrap ha-sec-head">
        <h2 className="ha-h2">Leer onze zorgverleners kennen.</h2>
        <Link to={R.zoeken} className="ha-link-more">Bekijk alle zorgverleners ›</Link>
      </div>
      <Carousel label="Zorgverleners" resetKey={String(faces.length)}>
        {faces.map((f) => (
          <article key={f.key} className="ha-face" style={{ background: f.bg }}>
            {f.photo ? (
              <img src={f.photo} alt={`Profielfoto van ${f.name}`} loading="lazy" onError={(event) => { event.currentTarget.style.display = "none"; }} />
            ) : (
              <svg className="ha-silhouette" viewBox="0 0 340 360" aria-hidden="true">
                <circle cx="170" cy="85" r="86" fill="rgba(0,0,0,.06)" />
                <path d="M0 360a170 170 0 0 1 340 0z" fill="rgba(0,0,0,.06)" />
              </svg>
            )}
            <div className="ha-face-shade" aria-hidden="true" />
            <div className="ha-face-text">
              <p className="ha-face-role">{f.role}</p>
              {f.quote && <h3>{f.quote}</h3>}
            </div>
            {!f.photo && <span className="ha-face-name">{f.name}</span>}
            <PlusLink to={f.to} label={`Bekijk profiel van ${f.name}`} />
          </article>
        ))}
      </Carousel>
    </section>
  );
}

/* ─── 7-8. Hulpvraag-quiz → gefilterde zorgverleners ──────────────────── */
function HelpQuiz() {
  const navigate = useNavigate();
  const [needs, setNeeds] = useState<string[]>([]);
  const [who, setWho] = useState<string | null>(null);
  const [plaats, setPlaats] = useState("");
  const toggle = (n: string) => setNeeds((cur) => (cur.includes(n) ? cur.filter((x) => x !== n) : [...cur, n]));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const q = new URLSearchParams();
    if (needs.length) q.set("zorg", needs.join(","));
    if (who) q.set("voor", who);
    if (plaats.trim()) q.set("plaats", plaats.trim().slice(0, 60));
    navigate(`${R.zoeken}${q.toString() ? `?${q}` : ""}`);
  };

  return (
    <section id="hulpvraag" style={{ scrollMarginTop: 64 }}>
      <div className="ha-wrap ha-help">
        <CircleHelp size={48} color="var(--ha-accent)" strokeWidth={1.4} aria-hidden="true" style={{ margin: "0 auto" }} />
        <h2 className="ha-h2-lg" style={{ marginTop: 20 }}>Ik heb hulp nodig.</h2>
        <p>Beantwoord drie korte vragen. Je ziet direct welke zorgverleners bij je passen.</p>
      </div>
      <div className="ha-wrap" style={{ paddingBottom: 96 }}>
        <form className="ha-quiz" onSubmit={submit}>
          <span className="ha-quiz-step">1. Zorgsoort</span>
          <h3>Waarbij kunnen we helpen?</h3>
          <p className="ha-quiz-count" aria-live="polite">Kies alles wat van toepassing is · {needs.length} gekozen</p>
          <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className="ha-sr">Zorgbehoeften</legend>
            <div className="ha-grid4">
              {CARE_NEEDS.map((n) => {
                const on = needs.includes(n.key);
                return (
                  <label key={n.key} className={`ha-tile ${on ? "is-on" : ""}`}>
                    <input type="checkbox" checked={on} onChange={() => toggle(n.key)} />
                    {n.label}
                    <span className="ha-tile-dot" aria-hidden="true">{on && <Check size={14} strokeWidth={3} />}</span>
                  </label>
                );
              })}
            </div>
          </fieldset>
          <h4 id="ha-who">2. Voor wie is de zorg?</h4>
          <div className="ha-pills" role="radiogroup" aria-labelledby="ha-who">
            {FOR_WHO_OPTIONS.map((w) => (
              <button key={w.key} type="button" role="radio" aria-checked={who === w.key} className="ha-pill" onClick={() => setWho(who === w.key ? null : w.key)}>
                {w.label}
              </button>
            ))}
          </div>
          <label htmlFor="ha-postcode" className="ha-label">3. In welke plaats?</label>
          <div className="ha-postcode">
            <input
              id="ha-postcode"
              value={plaats}
              onChange={(e) => setPlaats(e.target.value)}
              placeholder="Bijv. Utrecht"
              autoComplete="address-level2"
              maxLength={60}
            />
            <button type="submit" className="ha-btn ha-btn-lg">Bekijk zorgverleners in mijn buurt</button>
          </div>
          <p className="ha-quiz-foot">Liever even overleggen? Mail ons via <a href={CONTACT_HREF}>{CONTACT_EMAIL}</a>.</p>
        </form>
      </div>
    </section>
  );
}

/* ─── 9. Donker blok ─────────────────────────────────────────────────── */
type JobPosting = { id: string; category: string | null; city: string | null };
const FALLBACK_ROWS = [
  { l: "Begeleiding · 2 dagdelen", r: "Kampen" },
  { l: "Persoonlijke verzorging · ochtend", r: "Zwolle" },
  { l: "Inwonende zorg · 24 uur", r: "Deventer" },
];
function ForCaregivers() {
  const { data: jobs = [] } = useQuery({
    queryKey: ["homepage-job-postings", null],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("get-homepage-job-postings", {});
      if (error) throw error;
      return (data?.jobPostings ?? []) as JobPosting[];
    },
    staleTime: 10 * 60 * 1000,
  });
  const realRows = jobs.filter((j) => j.category && j.city).slice(0, 3).map((j) => ({ l: pretty(j.category!), r: j.city! }));
  const rows = realRows.length === 3 ? realRows : FALLBACK_ROWS;

  return (
    <section className="ha-darksec" id="voor-zorgverleners">
      <div className="ha-wrap">
        <div className="ha-sec-head" style={{ marginBottom: 0 }}>
          <h2 className="ha-h2-lg">Zorgverlener?<br />Laat cliënten jou vinden.</h2>
          <Link to={R.abonnementen} className="ha-link-more">Bekijk abonnementen ›</Link>
        </div>
        <div className="ha-dark-grid">
          <article className="ha-dcard">
            <p className="ha-dcard-label">Jouw profiel</p>
            <h3>Jouw eigen website.<br />Zonder er één te bouwen.</h3>
            <p>Je profiel met foto, ervaring en werkgebied wordt gevonden door cliënten die op het platform zoeken. Jij lift mee op ons bereik.</p>
            <div className="ha-mini" aria-hidden="true">
              <span className="ha-mini-avatar" />
              <div><strong>[Jouw naam]</strong><small>Verzorgende IG · Zwolle en omgeving</small></div>
              <span className="ha-badge">Beschikbaar</span>
            </div>
          </article>
          <article className="ha-dcard">
            <p className="ha-dcard-label">Direct contact</p>
            <h3>Geen bemiddeling.<br />Directe PGB-cliënten.</h3>
            <p>Cliënten plaatsen een hulpvraag, jij reageert. Jullie maken samen afspraken. Ook in jouw regio.</p>
            <ul className="ha-rows" style={{ listStyle: "none", padding: 0, margin: "auto 0 0" }} aria-label="Voorbeelden van hulpvragen">
              {rows.map((r, i) => (
                <li key={i} className="ha-row"><span>{r.l}</span><span>{r.r}</span></li>
              ))}
            </ul>
          </article>
        </div>
        <div className="ha-dark-cta">
          <Link to={R.aanmelden} className="ha-btn ha-btn-lg ha-btn-dark">Meld je aan als zorgverlener</Link>
          <Link to={R.contact} style={{ fontSize: 17 }}>Kom langs voor koffie in Zwolle ›</Link>
        </div>
      </div>
    </section>
  );
}

/* ─── 10. FAQ ────────────────────────────────────────────────────────── */
export const HOME_FAQ: [string, string][] = [
  ["Is Perfecte Zorgmatch een bemiddelingsbureau?", "Nee. Wij brengen cliënten en zorgverleners met elkaar in contact. Jullie maken samen afspraken, zonder tussenpersoon."],
  ["Wat kost het om een hulpvraag te plaatsen?", "Een hulpvraag plaatsen is gratis. Zorgverleners nemen een abonnement om op hulpvragen te reageren."],
  ["Zijn er zorgverleners in mijn regio?", "Ja. Vul je postcode in en zie direct welke zorgverleners bij jou in de buurt werken."],
  ["Kan ik de zorg betalen met mijn PGB?", "Ja. Met een persoonsgebonden budget kies je zelf wie je zorg geeft. Op Perfecte Zorgmatch vind je zorgverleners die rechtstreeks met PGB-cliënten werken."],
  ["Hoe weet ik wie er bij mij binnenkomt?", "Ieder profiel toont een foto, ervaring en werkgebied. Maak altijd eerst vrijblijvend kennis voordat je afspraken maakt."],
];
function Faq() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section className="ha-faq" id="faq">
      <h2 className="ha-h2">Alles wat je wilt weten.</h2>
      <div className="ha-faq-list">
        {HOME_FAQ.map(([q, a], i) => (
          <div key={q} className="ha-faq-item">
            <h3 style={{ margin: 0 }}>
              <button
                type="button"
                className="ha-faq-q"
                aria-expanded={open === i}
                aria-controls={`ha-faq-${i}`}
                onClick={() => setOpen(open === i ? null : i)}
              >
                {q}
                <ChevronDown size={20} aria-hidden="true" />
              </button>
            </h3>
            {open === i && <p id={`ha-faq-${i}`} className="ha-faq-a">{a}</p>}
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─── 11. Footer ─────────────────────────────────────────────────────── */
export function HomeFooter() {
  const onHome = useLocation().pathname === "/";
  const cols: [string, [string, string][]][] = [
    ["Zorg vinden", [["Alle zorgsoorten", R.zorgsoorten], ["Zorgverleners", R.zoeken], ["Plaats een hulpvraag", R.hulpvraag], ["Help me kiezen", "#hulpvraag"], ["Zorg met een PGB", R.pgb], ["PGB aanvragen", R.pgbAanvragen]]],
    ["Voor zorgverleners", [["Aanmelden", R.aanmelden], ["Abonnementen", R.abonnementen], ["Inloggen", R.login], ["Partners", R.partners]]],
    ["Over ons", [["Verhalen uit de zorg", R.verhalen], ["Ons kantoor in Zwolle", R.contact], ["Veelgestelde vragen", "#faq"], ["Algemene voorwaarden", R.voorwaarden], ["Privacybeleid", "/privacy"]]],
    ["Contact", [["Stuur een bericht", R.contact]]],
  ];
  return (
    <footer className="ha-foot">
      <div className="ha-wrap">
        <div className="ha-foot-top">
          <Heart size={32} color="var(--ha-accent)" strokeWidth={1.6} aria-hidden="true" style={{ margin: "0 auto" }} />
          <h2>Zorg die past.</h2>
        </div>
        <div className="ha-foot-cols">
          {cols.map(([title, links]) => (
            <nav key={title} aria-label={title}>
              <h3>{title}</h3>
              <ul>
                {links.map(([label, to]) => (
                  <li key={label}>
                     {to.startsWith("#") && onHome ? <a href={to}>{label}</a> : <Link to={to.startsWith("#") ? `/${to}` : to}>{label}</Link>}
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>
        <div className="ha-foot-bottom">
          <span>Perfecte Zorgmatch is geen bemiddelingsbureau. Zorgverleners werken zelfstandig.</span>
          <span>
            <button type="button" className="ha-foot-cookie" onClick={() => window.dispatchEvent(new CustomEvent(TRACKING_WINDOW_EVENTS.COOKIE_BANNER_REOPEN))}>Cookie-instellingen</button>
            {" · "}© 2026 Perfecte Zorgmatch
            <span style={{ display: "block", marginTop: 12 }}><ParentCompanyBadge /></span>
          </span>
        </div>
      </div>
    </footer>
  );
}

/* ─── Pagina ─────────────────────────────────────────────────────────── */
export function useInstrumentSans() {
  useEffect(() => {
    const id = "ha-font-instrument-sans";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Instrument+Sans:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
  }, []);
}

export default function HomeApple({ nav }: { nav?: ReactNode }) {
  useInstrumentSans();
  return (
    <div className={`ha${nav ? " ha--fixed-nav" : ""}`}>
      {nav ?? <HomeNav />}
      <main>
        <Hero />
        <CareTypes />
        <WhyUs />
        <Faces />
        <HelpQuiz />
        <ForCaregivers />
        <Faq />
      </main>
      <HomeFooter />
    </div>
  );
}
